import React, { useCallback, useEffect, useState } from 'react'
import className from 'classnames'
import { Icon } from '@lightspeed/design-system-react'
import { vn } from 'vend-number'
import { useT } from '@transifex/react'
import { ApiProduct } from '@vend/types'
import { LineItem } from '../../../models/line-item'
import { DeleteButton } from '../../shared/DeleteButton'
import { dateFormat } from '../../../filters/wr-date-format'
import { displayRound } from '../../../filters/display-round'
import {
  useProductsStore,
  useRegisterManager,
  useSaleInventoryData,
  useVdFeatureManager,
} from '../../../ng-compat/hooks'
import {
  getVariantsDisplayNames,
  hasVariants,
  isDeletedProduct,
  isPriceDiscounted,
  isGiftCard,
} from '../../../functions/lineItem'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import {
  openFulfilmentLineItemPopover,
  resetExpandedLineItemId,
  selectExpandedLineItemId,
  selectOpenFulfilmentPopoverLineItemId,
  setExpandedLineItemId,
} from '../../../state/slices/uiStateSlice'
import {
  FulfilmentDetail,
  isDefinedFulfilmentType,
  FulfilmentType,
} from '../../../models/fulfilment'
import { EVENT } from '../../../constants'
import {
  selectFulfilmentNoteIsInvalid,
  selectSaleAttributesHasFulfilment,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { selectSaleReturnFor } from '../../../state/selectors'
import { useSaleHasServiceOrder } from '../../sale/useSaleHasServiceOrder'
import { useSaleIsContinuedServiceSale } from '../../sale/useSaleIsContinuedServiceSale'
import { useSpecialOrderProduct } from '../../special-order-product/useSpecialOrderProduct'
import { LineItemTotalPrice } from './LineItemTotalPrice'
import { LineItemTotalBasePrice } from './LineItemTotalBasePrice'
import { LineItemCustomFields } from './LineItemCustomFields'
import { LineItemFulfilmentPopover } from './fulfilment/popover/LineItemFulfilmentPopover'
import { useHasOutdatedTaxCategory } from './fulfilment/hook/useHasOutdatedTaxCategory'
import { useSaleFulfilment } from './fulfilment/hook/useSaleFulfilment'
import { CurrentView } from './fulfilment/popover/LineItemFulfillmentContext'
import {
  LineItemLockedIcon,
  LineItemLockedTooltipVariant,
} from './LineItemLockedIcon'
import { useLineItemBreakdownEligibility } from './useLineItemBreakdownEligibility'
import { useProductPackagingBreakdown } from './useProductPackagingBreakdown'
import { SpecialOrderProductTag } from './SpecialOrderProductTag'

interface Props {
  hasInvalidInput: boolean
  lineItem: LineItem
  editAllowedForConfirmed?: boolean
  tooltipVariant?: LineItemLockedTooltipVariant
  onRemove: () => void
  onToggle: (event: React.MouseEvent) => void
  onSelectContinueAnyway?: () => void
  onSelectFulfilmentType?: (
    type: FulfilmentType,
    fulfilmentOutletId?: string
  ) => void
}

const _context = 'Sell screen line item'

export const LineItemDisplay: React.FC<Props> = ({
  hasInvalidInput,
  lineItem,
  editAllowedForConfirmed = false,
  tooltipVariant = 'default',
  onRemove,
  onToggle,
  onSelectContinueAnyway,
  onSelectFulfilmentType,
}) => {
  const t = useT()
  const dispatch = useAppDispatch()
  const registerManager = useRegisterManager()
  const outlet = registerManager.getActiveOutlet()
  const outletTimeZone = outlet && outlet.time_zone
  const isReferencedReturn = !!useAppSelector(selectSaleReturnFor)

  const isSalesPersonFFEnabled = useVdFeatureManager().get(
    'sales_person_attribution'
  )

  const readonly = isGiftCard(lineItem.product)
  const expandedLineItemId = useAppSelector(selectExpandedLineItemId)
  const isExpanded = expandedLineItemId === lineItem.localId

  const fulfilmentPopoverLineItemId = useAppSelector(
    selectOpenFulfilmentPopoverLineItemId
  )
  const fulfilmentPopoverOpen = fulfilmentPopoverLineItemId === lineItem.localId

  const { setFulfilmentType, fulfilmentDetail } = useSaleFulfilment(
    lineItem.localId
  )

  const saleInventoryData = useSaleInventoryData()
  const productStore = useProductsStore()

  const saleHasServiceOrder = useSaleHasServiceOrder()
  const isContinuedServiceSale = useSaleIsContinuedServiceSale()

  const fulfilmentDetailsRequired =
    !isDefinedFulfilmentType(lineItem.fulfilmentType) &&
    saleInventoryData.shouldValidateInventory(lineItem) &&
    !saleInventoryData.hasInventory(lineItem) &&
    // The service order sales deal with the fulfillment in jaws so we don't need to show the error
    !saleHasServiceOrder

  const isFulfilmentNoteInvalid = useAppSelector(selectFulfilmentNoteIsInvalid)
  const hasFulfilmentAttributes = useAppSelector(
    selectSaleAttributesHasFulfilment
  )

  const { canShowBreakdownOption } = useLineItemBreakdownEligibility(lineItem)

  const [outerProduct, setOuterProduct] = useState<ApiProduct>()

  useEffect(() => {
    const getProduct = async () => {
      const sourceProductID =
        lineItem.product.packaging?.made_from[0]?.source_product_id
      if (canShowBreakdownOption && sourceProductID) {
        const product = await productStore.get(sourceProductID)
        setOuterProduct(product)
      }
    }

    getProduct()
  }, [lineItem, productStore, canShowBreakdownOption])

  const onToggleLocal = (e: React.MouseEvent<Element, MouseEvent>) => {
    e.stopPropagation()

    // Don't block expansion if the popover is hidden for continued service sales
    const isPopoverActuallyOpen =
      fulfilmentPopoverOpen && !isContinuedServiceSale

    if (isPopoverActuallyOpen) {
      return
    }

    dispatch(
      isExpanded
        ? resetExpandedLineItemId()
        : setExpandedLineItemId(lineItem.localId)
    )
    onToggle(e)
  }

  const onFulfilmentDetailsSet = useCallback(
    (fulfilmentDetails: FulfilmentDetail, fulfilmentOutletId?: string) => {
      setFulfilmentType(
        fulfilmentDetails.type,
        fulfilmentDetails.note,
        fulfilmentOutletId
      )
      saleInventoryData.disableInventoryValidation(lineItem)
      saleInventoryData.emit(EVENT.LINE_ITEM_UPDATE_VALIDATION)
    },
    [setFulfilmentType, saleInventoryData, lineItem]
  )

  const onClosePopover = () => dispatch(openFulfilmentLineItemPopover(''))

  const productInventoryLevel =
    saleInventoryData.getInventoryData(lineItem)?.inventoryLevel ?? 0

  const {
    breakdownOuterPacks,
    loading: isBreakingDown,
  } = useProductPackagingBreakdown(
    lineItem,
    outlet.id,
    productInventoryLevel || 0,
    canShowBreakdownOption
  )

  const hasOutdatedTaxCategory =
    useHasOutdatedTaxCategory(lineItem.id || lineItem.localId) &&
    lineItem.fulfilmentType === FulfilmentType.DELIVERY

  const showErrorIcon =
    hasInvalidInput ||
    fulfilmentDetailsRequired ||
    hasOutdatedTaxCategory ||
    (isDefinedFulfilmentType(lineItem.fulfilmentType) &&
      isFulfilmentNoteInvalid &&
      !hasFulfilmentAttributes)

  const locked = !editAllowedForConfirmed && lineItem.confirmed

  const isSpecialOrderProduct = useSpecialOrderProduct(lineItem)
  const showSpecialOrderProductTag = isSpecialOrderProduct(lineItem)

  return (
    <ul>
      <li
        // a proper fix would involve setting this tabIndex and `on*` callbacks to an
        // interactive element wrapping all the elements inside this `li` except
        // for the element tied to the delete action.
        // This involves styling changes and some logic is built on those styles,
        // so at this point we might as well leave this as is knowing this will get
        // rebuild on React and this mistake can be avoided when we do that.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        className={className('li-cell-summary-container', 'vd-pb4', {
          'li-cell-summary-container--readonly': readonly,
        })}
        onClick={onToggleLocal}
        id={`wr-sale-line-item-${lineItem.id || lineItem.localId}`}
      >
        <span
          tabIndex={0}
          role="button"
          aria-pressed="false"
          className="vd-pl1 vd-pr1 vd-pb4"
        >
          <Icon
            className={className('vd-text--secondary wr-expandable-chevron')}
            icon={isExpanded ? 'angle-down' : 'angle-right'}
          />
        </span>

        <div className="li-cell-summary li-cell-summary--quantity">
          {!saleInventoryData.isLoading && !lineItem.calculating && (
            <LineItemFulfilmentPopover
              open={
                fulfilmentPopoverOpen &&
                !isReferencedReturn &&
                !isContinuedServiceSale
              }
              productName={lineItem.product.name}
              productId={lineItem.product.id}
              productQuantity={productInventoryLevel}
              onFulfilmentDetailsSet={onFulfilmentDetailsSet}
              onClose={onClosePopover}
              defaultView={
                saleInventoryData.shouldValidateInventory(lineItem) &&
                !saleInventoryData.hasInventory(lineItem)
                  ? CurrentView.DECISION
                  : undefined
              }
              defaultFulfilmentDetail={fulfilmentDetail}
              onSelectContinueAnyway={onSelectContinueAnyway}
              onSelectFulfilmentType={onSelectFulfilmentType}
              canShowBreakdownOption={canShowBreakdownOption}
              outerProduct={outerProduct}
              breakdownOuterPacks={breakdownOuterPacks}
              isBreakingDown={isBreakingDown}
              showSpecialOrderMessage={showSpecialOrderProductTag}
            >
              {showErrorIcon ? (
                <span data-testid="line-item-icon-error">
                  <Icon
                    icon="exclamation-triangle"
                    className="wr-input-error-icon"
                    title={t('There is a problem with this item', {
                      _context,
                    })}
                  />
                </span>
              ) : (
                <QuantitySummaryText quantity={lineItem.quantity} />
              )}
            </LineItemFulfilmentPopover>
          )}
        </div>

        <div
          className={className(
            'li-cell-summary li-cell-summary--name-container ',
            {
              'vd-pb2': showSpecialOrderProductTag,
            }
          )}
        >
          <div
            className="li-cell-summary pro-li-cell-summary-name vd-text-label"
            title={lineItem.product.name}
          >
            {lineItem.product.name}

            {hasVariants(lineItem) && (
              <div className="vd-text-supplementary">
                {getVariantsDisplayNames(lineItem)}
              </div>
            )}

            {isSalesPersonFFEnabled && lineItem.salesperson && (
              <div className="vd-text-supplementary">
                {t('Sold by: {salesPerson}', {
                  _context,
                  salesPerson: lineItem.salesperson.displayName,
                })}
              </div>
            )}

            {isDeletedProduct(lineItem) && (
              <div className="vd-text-supplementary">
                Deleted on{' '}
                {dateFormat(
                  lineItem.product.deleted_at,
                  'DD MMMM YYYY',
                  outletTimeZone
                )}
              </div>
            )}

            {lineItem.giftCardNumber && (
              <div className="vd-text-supplementary">
                {t('Card Number: {giftCardNumber}', {
                  _context,
                  giftCardNumber: lineItem.giftCardNumber,
                })}
              </div>
            )}

            {lineItem.promotionDetails &&
              lineItem.promotionDetails.map(promotionDetail => (
                <div
                  key={promotionDetail.id!}
                  className="vd-text-supplementary"
                >
                  <Icon
                    icon="ticket"
                    className="li-cell-actions-promotion-icon"
                  />
                  <span className="pro-promotion-detail-name">
                    {promotionDetail.name}
                  </span>
                </div>
              ))}
          </div>

          <LineItemCustomFields lineItem={lineItem} />
          {showSpecialOrderProductTag && (
            <SpecialOrderProductTag className="vd-pt2" />
          )}
        </div>

        <div className="li-cell-summary--totals-container">
          {lineItem.calculating ? (
            <span className="vd-loader vd-loader--small" />
          ) : (
            <div>
              <div className="li-cell-summary--total-price">
                <LineItemTotalPrice totals={lineItem.totals} />
              </div>
              {isPriceDiscounted(lineItem) && (
                <div className="li-cell-summary--total-base-price">
                  <LineItemTotalBasePrice totals={lineItem.totals} />
                </div>
              )}
            </div>
          )}
        </div>

        <div
          className="trash-button-area vd-ml2"
          data-cy="li-cell-summary--remove"
        >
          {locked ? (
            <LineItemLockedIcon
              lineItemID={lineItem.localId}
              tooltipVariant={tooltipVariant}
            />
          ) : (
            <DeleteButton
              onClick={onRemove}
              modifier="icon"
              variant="no"
              aria-label="Delete product"
              customClasses={{ root: 'relative top-1' }}
            />
          )}
        </div>
      </li>
    </ul>
  )
}

export const QuantitySummaryText: React.FC<{
  quantity: string | number
  prefix?: string
}> = ({ quantity, prefix = '' }) => (
  <div
    className={className('', {
      'li-cell-summary--quantity-negative': vn(quantity).lessThan(0),
    })}
  >
    <span className="li-cell-summary--quantity-text">
      {prefix}
      {displayRound(quantity, 3, true)}
    </span>
  </div>
)
